import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FiCalendar,
  FiPrinter,
  FiDownload,
  FiRefreshCw,
  FiTruck,
  FiUsers,
  FiShoppingBag,
  FiDollarSign,
  FiTrendingUp,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
  FiArrowUpRight,
  FiArrowDownRight,
  FiPercent,
  FiCheckCircle,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { dashboardApi, purchaseApi, saleApi } from "../services/api";
import logoImg from "../assets/Icons/Logo.png";

// Helper for DD-MM-YYYY display
const formatDateDisplay = (dateStr) => {
  if (!dateStr || dateStr === "—") return "—";
  try {
    if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const parts = dateStr.slice(0, 10).split("-");
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return String(dateStr);
  }
};

// Format currency
const formatRs = (num) => {
  const val = parseFloat(num) || 0;
  return val.toLocaleString("en-PK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const SummaryPage = () => {
  const { user } = useAuth();

  // Helper to format date as YYYY-MM-DD for input
  const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Default: Start of this month to Today
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(toYMD(firstDayOfMonth));
  const [endDate, setEndDate] = useState(toYMD(today));
  const [activePreset, setActivePreset] = useState("thisMonth");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'purchases' | 'sales'

  const [loading, setLoading] = useState(true);
  const [rawVendors, setRawVendors] = useState([]);
  const [rawClients, setRawClients] = useState([]);
  const [expandedVendors, setExpandedVendors] = useState(new Set());
  const [expandedClients, setExpandedClients] = useState(new Set());

  // Quick Date Presets
  const applyPreset = (preset) => {
    setActivePreset(preset);
    const now = new Date();

    if (preset === "today") {
      const s = toYMD(now);
      setStartDate(s);
      setEndDate(s);
    } else if (preset === "yesterday") {
      const yest = new Date();
      yest.setDate(now.getDate() - 1);
      const s = toYMD(yest);
      setStartDate(s);
      setEndDate(s);
    } else if (preset === "last7") {
      const past = new Date();
      past.setDate(now.getDate() - 6);
      setStartDate(toYMD(past));
      setEndDate(toYMD(now));
    } else if (preset === "thisMonth") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(toYMD(first));
      setEndDate(toYMD(now));
    } else if (preset === "lastMonth") {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      setStartDate(toYMD(first));
      setEndDate(toYMD(last));
    } else if (preset === "thisYear") {
      const first = new Date(now.getFullYear(), 0, 1);
      setStartDate(toYMD(first));
      setEndDate(toYMD(now));
    } else if (preset === "all") {
      setStartDate("");
      setEndDate("");
    }
  };

  // Fetch and Aggregate Summary Data
  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      params.limit = 10000;

      let fetchedFromDedicated = false;

      // 1. Try dedicated business-summary endpoint first
      try {
        const backendRes = await dashboardApi.getBusinessSummary(params);
        if (backendRes && (backendRes.data || backendRes.vendors)) {
          const vList = backendRes.data?.vendors || backendRes.vendors || [];
          const cList = backendRes.data?.clients || backendRes.clients || [];
          setRawVendors(vList);
          setRawClients(cList);
          fetchedFromDedicated = true;
        }
      } catch (backendErr) {
        // Fallback to client-side aggregation from purchases & sales APIs
        fetchedFromDedicated = false;
      }

      // 2. Fallback: Aggregate from Purchases & Sales APIs
      if (!fetchedFromDedicated) {
        const [purchasesRes, salesRes] = await Promise.all([
          purchaseApi.getAll(params).catch(() => ({ data: [] })),
          saleApi.getAll(params).catch(() => ({ data: [] })),
        ]);

        const purchasesList = Array.isArray(purchasesRes?.data)
          ? purchasesRes.data
          : Array.isArray(purchasesRes)
          ? purchasesRes
          : [];

        const salesList = Array.isArray(salesRes?.data)
          ? salesRes.data
          : Array.isArray(salesRes)
          ? salesRes
          : [];

        // Aggregate Vendors
        const vendorMap = {};
        purchasesList.forEach((p) => {
          const vKey = p.vendor_id ? String(p.vendor_id) : p.vendor_name || "Unknown";
          if (!vendorMap[vKey]) {
            vendorMap[vKey] = {
              vendor_id: p.vendor_id,
              vendor_name: p.vendor_name || "Vendor #" + vKey,
              contact_person_name: p.vendor_contact_person || "",
              contact: p.vendor_contact || "",
              ntn: p.vendor_ntn || "",
              total_bills: 0,
              total_items: 0,
              total_amount: 0,
              bills: [],
            };
          }
          const itemsCount = parseInt(p.total_items_count ?? p.quantity ?? 1) || 0;
          const amt = parseFloat(p.total_amount || 0) || 0;

          vendorMap[vKey].total_bills += 1;
          vendorMap[vKey].total_items += itemsCount;
          vendorMap[vKey].total_amount += amt;
          vendorMap[vKey].bills.push({
            id: p.id,
            bill_number: p.bill_number,
            purchase_date: p.purchase_date || p.created_at,
            total_items: itemsCount,
            total_amount: amt,
          });
        });

        // Aggregate Clients
        const clientMap = {};
        salesList.forEach((sa) => {
          const cKey = sa.client_id ? String(sa.client_id) : sa.client_name || "Unknown";
          if (!clientMap[cKey]) {
            clientMap[cKey] = {
              client_id: sa.client_id,
              client_name: sa.client_name || "Client #" + cKey,
              contact_person_name: sa.client_contact_person || "",
              contact: sa.client_contact || "",
              ntn: sa.client_ntn || "",
              total_invoices: 0,
              total_items: 0,
              total_amount: 0,
              invoices: [],
            };
          }
          const itemsCount = parseInt(sa.total_items_count ?? sa.quantity ?? 1) || 0;
          const amt = parseFloat(sa.total_amount || 0) || 0;

          clientMap[cKey].total_invoices += 1;
          clientMap[cKey].total_items += itemsCount;
          clientMap[cKey].total_amount += amt;
          clientMap[cKey].invoices.push({
            id: sa.id,
            invoice_number: sa.invoice_number,
            sale_date: sa.sale_date || sa.created_at,
            total_items: itemsCount,
            total_amount: amt,
          });
        });

        const vArr = Object.values(vendorMap).sort((a, b) => b.total_amount - a.total_amount);
        const cArr = Object.values(clientMap).sort((a, b) => b.total_amount - a.total_amount);

        setRawVendors(vArr);
        setRawClients(cArr);
      }
    } catch (err) {
      console.error("Summary fetch error:", err);
      toast.error("Failed to load summary data for the selected range");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Filtered lists based on search
  const filteredVendors = useMemo(() => {
    if (!searchTerm.trim()) return rawVendors;
    const q = searchTerm.trim().toLowerCase();
    return rawVendors.filter(
      (v) =>
        (v.vendor_name && v.vendor_name.toLowerCase().includes(q)) ||
        (v.contact_person_name && v.contact_person_name.toLowerCase().includes(q)) ||
        (v.contact && v.contact.toLowerCase().includes(q))
    );
  }, [rawVendors, searchTerm]);

  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) return rawClients;
    const q = searchTerm.trim().toLowerCase();
    return rawClients.filter(
      (c) =>
        (c.client_name && c.client_name.toLowerCase().includes(q)) ||
        (c.contact_person_name && c.contact_person_name.toLowerCase().includes(q)) ||
        (c.contact && c.contact.toLowerCase().includes(q))
    );
  }, [rawClients, searchTerm]);

  // Grand Totals Calculation
  const grandTotals = useMemo(() => {
    const totalVendorCount = filteredVendors.length;
    const totalPurchaseBills = filteredVendors.reduce((acc, v) => acc + (parseInt(v.total_bills) || 0), 0);
    const totalPurchasedItems = filteredVendors.reduce((acc, v) => acc + (parseInt(v.total_items) || 0), 0);
    const totalPurchaseAmount = filteredVendors.reduce((acc, v) => acc + (parseFloat(v.total_amount) || 0), 0);

    const totalClientCount = filteredClients.length;
    const totalSaleInvoices = filteredClients.reduce((acc, c) => acc + (parseInt(c.total_invoices) || 0), 0);
    const totalSoldItems = filteredClients.reduce((acc, c) => acc + (parseInt(c.total_items) || 0), 0);
    const totalSaleAmount = filteredClients.reduce((acc, c) => acc + (parseFloat(c.total_amount) || 0), 0);

    const netAmountDiff = totalSaleAmount - totalPurchaseAmount;
    const netItemsDiff = totalPurchasedItems - totalSoldItems;

    return {
      purchases: {
        vendorCount: totalVendorCount,
        billsCount: totalPurchaseBills,
        itemsCount: totalPurchasedItems,
        amount: totalPurchaseAmount,
      },
      sales: {
        clientCount: totalClientCount,
        invoicesCount: totalSaleInvoices,
        itemsCount: totalSoldItems,
        amount: totalSaleAmount,
      },
      net: {
        amountDiff: netAmountDiff,
        itemsDiff: netItemsDiff,
      },
    };
  }, [filteredVendors, filteredClients]);

  // Toggle Vendor Expansion
  const toggleVendorExpand = (vendorId) => {
    setExpandedVendors((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });
  };

  // Toggle Client Expansion
  const toggleClientExpand = (clientId) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  // Print Action
  const handlePrint = () => {
    window.print();
  };

  // CSV Export Action
  const handleExportCsv = () => {
    let csv = "COMMERCIAL IT SOLUTIONS - BUSINESS SUMMARY REPORT\r\n";
    csv += `Period: ${formatDateDisplay(startDate)} to ${formatDateDisplay(endDate)}\r\n\r\n`;

    // 1. Vendor Purchases
    csv += "--- VENDOR PURCHASES SUMMARY ---\r\n";
    csv += "Sr #,Vendor Name,Contact Person,Phone,Total Bills,Total Items,Total Amount (Rs.)\r\n";
    filteredVendors.forEach((v, idx) => {
      csv += `${idx + 1},"${(v.vendor_name || "").replace(/"/g, '""')}","${(v.contact_person_name || "").replace(/"/g, '""')}","${v.contact || ""}",${v.total_bills},${v.total_items},${v.total_amount}\r\n`;
    });
    csv += `TOTAL,Total ${grandTotals.purchases.vendorCount} Vendors,,,Total ${grandTotals.purchases.billsCount} Bills,Total ${grandTotals.purchases.itemsCount} Items,Rs. ${grandTotals.purchases.amount}\r\n\r\n`;

    // 2. Client Sales
    csv += "--- CLIENT SALES SUMMARY ---\r\n";
    csv += "Sr #,Client Name,Contact Person,Phone,Total Invoices,Total Items,Total Sale Amount (Rs.)\r\n";
    filteredClients.forEach((c, idx) => {
      csv += `${idx + 1},"${(c.client_name || "").replace(/"/g, '""')}","${(c.contact_person_name || "").replace(/"/g, '""')}","${c.contact || ""}",${c.total_invoices},${c.total_items},${c.total_amount}\r\n`;
    });
    csv += `TOTAL,Total ${grandTotals.sales.clientCount} Clients,,,Total ${grandTotals.sales.invoicesCount} Invoices,Total ${grandTotals.sales.itemsCount} Items,Rs. ${grandTotals.sales.amount}\r\n\r\n`;

    // 3. Overall Net
    csv += "--- EXECUTIVE OVERVIEW ---\r\n";
    csv += `Total Purchases Amount,Rs. ${grandTotals.purchases.amount}\r\n`;
    csv += `Total Sales Amount,Rs. ${grandTotals.sales.amount}\r\n`;
    csv += `Net Balance (Sales - Purchases),Rs. ${grandTotals.net.amountDiff}\r\n`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Business_Summary_${startDate || "All"}_to_${endDate || "All"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Summary report downloaded successfully");
  };

  const periodLabel = useMemo(() => {
    if (!startDate && !endDate) return "All Available Time";
    if (startDate && endDate) {
      if (startDate === endDate) return formatDateDisplay(startDate);
      return `${formatDateDisplay(startDate)} to ${formatDateDisplay(endDate)}`;
    }
    if (startDate) return `From ${formatDateDisplay(startDate)}`;
    return `Up to ${formatDateDisplay(endDate)}`;
  }, [startDate, endDate]);

  return (
    <div className="summary-page-container" style={{ padding: "24px", maxWidth: 1400, margin: "0 auto" }}>
      {/* PRINT STYLES */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm 10mm;
          }
          body {
            background-color: #ffffff !important;
            color: #000000 !important;
            font-size: 9.5pt !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print,
          .navbar-header,
          .app-sidebar,
          .sidebar,
          button,
          .quick-presets,
          .date-filter-box,
          .search-box-summary,
          .tab-nav-summary {
            display: none !important;
          }
          .summary-page-container {
            padding: 0 !important;
            max-width: 100% !important;
            margin: 0 !important;
          }
          .print-only {
            display: block !important;
          }
          .print-header {
            display: flex !important;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #0F172A;
            padding-bottom: 12px;
            margin-bottom: 16px;
          }
          .summary-kpi-card {
            border: 1px solid #CBD5E1 !important;
            box-shadow: none !important;
            padding: 10px !important;
            page-break-inside: avoid;
          }
          .table-section {
            page-break-inside: auto;
            margin-bottom: 20px !important;
          }
          .summary-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 9pt !important;
            page-break-inside: auto;
          }
          .summary-table th,
          .summary-table td {
            border: 1px solid #CBD5E1 !important;
            padding: 5px 8px !important;
          }
          .summary-table th {
            background-color: #F1F5F9 !important;
            color: #0F172A !important;
            font-weight: 800 !important;
          }
          .summary-table tfoot tr {
            background-color: #E2E8F0 !important;
            font-weight: 900 !important;
          }
          .print-footer-signatures {
            display: flex !important;
            justify-content: space-between;
            margin-top: 36px;
            padding-top: 16px;
            page-break-inside: avoid;
          }
        }
        @media screen {
          .print-only {
            display: none;
          }
        }
      `}</style>

      {/* PRINT-ONLY OFFICIAL HEADER */}
      <div className="print-only print-header">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img
            src={logoImg}
            alt="Logo"
            style={{ height: 48, width: "auto", objectFit: "contain" }}
          />
          <div>
            <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 900, color: "#0F172A", letterSpacing: "0.5px" }}>
              COMMERCIAL IT SOLUTIONS
            </h2>
            <div style={{ fontSize: "0.82rem", color: "#475569", fontWeight: 600 }}>
              Business Summary Report & Party Ledger Breakdown
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right", fontSize: "0.8rem", color: "#334155" }}>
          <div>
            <strong>Report Period:</strong> {periodLabel}
          </div>
          <div>
            <strong>Generated Date:</strong> {new Date().toLocaleDateString("en-GB")} {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
          <div>
            <strong>Generated By:</strong> {user?.name || user?.username || "Authorized Staff"} ({user?.role || "Admin"})
          </div>
        </div>
      </div>

      {/* ON-SCREEN TOP TITLE BAR */}
      <div
        className="no-print"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 10,
                backgroundColor: "#EEF2FF",
                color: "#4F46E5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiTrendingUp size={22} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 900, color: "var(--navy, #0F172A)" }}>
                Business Summary
              </h1>
              <p style={{ margin: "2px 0 0 0", fontSize: "0.84rem", color: "var(--slate-500, #64748B)" }}>
                Vendor purchases vs. Client sales summary with quantity & amounts
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons: Print, Export, Refresh */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={fetchSummary}
            disabled={loading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "9px 15px",
              borderRadius: 8,
              border: "1.5px solid var(--slate-300, #CBD5E1)",
              backgroundColor: "#ffffff",
              color: "var(--slate-700, #334155)",
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            <FiRefreshCw size={15} className={loading ? "spin" : ""} /> Refresh
          </button>

          <button
            type="button"
            onClick={handleExportCsv}
            disabled={loading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "9px 16px",
              borderRadius: 8,
              border: "1.5px solid var(--slate-300, #CBD5E1)",
              backgroundColor: "#ffffff",
              color: "var(--navy, #0F172A)",
              fontSize: "0.85rem",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <FiDownload size={15} /> Export Sheet
          </button>

          <button
            type="button"
            onClick={handlePrint}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "9px 18px",
              borderRadius: 8,
              border: "none",
              backgroundColor: "var(--primary, #064E3B)",
              color: "#ffffff",
              fontSize: "0.86rem",
              fontWeight: 800,
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(6, 78, 59, 0.25)",
            }}
          >
            <FiPrinter size={16} /> Print Summary
          </button>
        </div>
      </div>

      {/* DATE RANGE FILTER BOX */}
      <div
        className="no-print date-filter-box"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: 12,
          border: "1px solid var(--slate-200, #E2E8F0)",
          padding: "16px 20px",
          marginBottom: 20,
          boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
          {/* Calendar Range Inputs */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--navy, #0F172A)", fontWeight: 700, fontSize: "0.88rem" }}>
              <FiCalendar size={18} style={{ color: "var(--primary, #064E3B)" }} /> Date Range:
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActivePreset("custom");
                }}
                style={{
                  padding: "7px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300, #CBD5E1)",
                  fontSize: "0.85rem",
                  color: "var(--navy, #0F172A)",
                  outline: "none",
                  fontWeight: 600,
                }}
              />
              <span style={{ color: "var(--slate-400)", fontWeight: 600, fontSize: "0.85rem" }}>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActivePreset("custom");
                }}
                style={{
                  padding: "7px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300, #CBD5E1)",
                  fontSize: "0.85rem",
                  color: "var(--navy, #0F172A)",
                  outline: "none",
                  fontWeight: 600,
                }}
              />
            </div>

            {/* Selected Period Badge */}
            <div
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                backgroundColor: "#F1F5F9",
                border: "1px solid #E2E8F0",
                fontSize: "0.8rem",
                color: "var(--slate-700, #334155)",
                fontWeight: 700,
              }}
            >
              Selected: <span style={{ color: "var(--primary, #064E3B)" }}>{periodLabel}</span>
            </div>
          </div>

          {/* Search Party / Vendor / Client */}
          <div className="search-box-summary" style={{ position: "relative", minWidth: 260 }}>
            <FiSearch
              size={15}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--slate-400)",
              }}
            />
            <input
              type="text"
              placeholder="Search vendor or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "7px 12px 7px 32px",
                borderRadius: 6,
                border: "1px solid var(--slate-300, #CBD5E1)",
                fontSize: "0.84rem",
                outline: "none",
              }}
            />
          </div>
        </div>

        {/* Quick Presets Buttons */}
        <div
          className="quick-presets"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            marginTop: 14,
            paddingTop: 12,
            borderTop: "1px solid var(--slate-100, #F1F5F9)",
          }}
        >
          <span style={{ fontSize: "0.76rem", fontWeight: 700, color: "var(--slate-500)", marginRight: 4 }}>
            QUICK PRESETS:
          </span>
          {[
            { id: "today", label: "Today" },
            { id: "yesterday", label: "Yesterday" },
            { id: "last7", label: "Last 7 Days" },
            { id: "thisMonth", label: "This Month" },
            { id: "lastMonth", label: "Last Month" },
            { id: "thisYear", label: "This Year" },
            { id: "all", label: "All Time" },
          ].map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset.id)}
              style={{
                padding: "4px 10px",
                borderRadius: 5,
                border: activePreset === preset.id ? "1px solid var(--primary, #064E3B)" : "1px solid var(--slate-200, #E2E8F0)",
                backgroundColor: activePreset === preset.id ? "var(--primary, #064E3B)" : "#ffffff",
                color: activePreset === preset.id ? "#ffffff" : "var(--slate-600, #475569)",
                fontSize: "0.78rem",
                fontWeight: activePreset === preset.id ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* TOP EXECUTIVE OVERVIEW KPI CARDS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {/* Purchases Summary Card */}
        <div
          className="summary-kpi-card"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 12,
            border: "1.5px solid #CBD5E1",
            padding: "18px 20px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            borderLeft: "5px solid #2563EB",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <span style={{ fontSize: "0.75rem", fontWeight: 800, textTransform: "uppercase", color: "#2563EB", letterSpacing: "0.5px" }}>
                TOTAL PURCHASES (INWARD)
              </span>
              <div style={{ fontSize: "1.55rem", fontWeight: 900, color: "var(--navy, #0F172A)", marginTop: 2 }}>
                Rs. {formatRs(grandTotals.purchases.amount)}
              </div>
            </div>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: "#EFF6FF",
                color: "#2563EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiTruck size={20} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", color: "var(--slate-600)" }}>
            <div>
              <strong>{grandTotals.purchases.vendorCount}</strong> Vendors
            </div>
            <div>•</div>
            <div>
              <strong>{grandTotals.purchases.billsCount}</strong> Bills
            </div>
            <div>•</div>
            <div>
              <strong>{grandTotals.purchases.itemsCount.toLocaleString()}</strong> Items Received
            </div>
          </div>
        </div>

        {/* Sales Summary Card */}
        <div
          className="summary-kpi-card"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 12,
            border: "1.5px solid #CBD5E1",
            padding: "18px 20px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            borderLeft: "5px solid #059669",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <span style={{ fontSize: "0.75rem", fontWeight: 800, textTransform: "uppercase", color: "#059669", letterSpacing: "0.5px" }}>
                TOTAL SALES (OUTWARD)
              </span>
              <div style={{ fontSize: "1.55rem", fontWeight: 900, color: "var(--navy, #0F172A)", marginTop: 2 }}>
                Rs. {formatRs(grandTotals.sales.amount)}
              </div>
            </div>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: "#ECFDF5",
                color: "#059669",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiShoppingBag size={20} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", color: "var(--slate-600)" }}>
            <div>
              <strong>{grandTotals.sales.clientCount}</strong> Clients
            </div>
            <div>•</div>
            <div>
              <strong>{grandTotals.sales.invoicesCount}</strong> Invoices
            </div>
            <div>•</div>
            <div>
              <strong>{grandTotals.sales.itemsCount.toLocaleString()}</strong> Items Sold
            </div>
          </div>
        </div>

        {/* Net Cash & Unit Margin Card */}
        <div
          className="summary-kpi-card"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 12,
            border: "1.5px solid #CBD5E1",
            padding: "18px 20px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            borderLeft: grandTotals.net.amountDiff >= 0 ? "5px solid #10B981" : "5px solid #EF4444",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <span style={{ fontSize: "0.75rem", fontWeight: 800, textTransform: "uppercase", color: grandTotals.net.amountDiff >= 0 ? "#10B981" : "#EF4444", letterSpacing: "0.5px" }}>
                NET DIFFERENCE (SALES - PURCHASES)
              </span>
              <div
                style={{
                  fontSize: "1.55rem",
                  fontWeight: 900,
                  color: grandTotals.net.amountDiff >= 0 ? "#065F46" : "#991B1B",
                  marginTop: 2,
                }}
              >
                Rs. {formatRs(grandTotals.net.amountDiff)}
              </div>
            </div>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: grandTotals.net.amountDiff >= 0 ? "#ECFDF5" : "#FEF2F2",
                color: grandTotals.net.amountDiff >= 0 ? "#10B981" : "#EF4444",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {grandTotals.net.amountDiff >= 0 ? <FiArrowUpRight size={22} /> : <FiArrowDownRight size={22} />}
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", color: "var(--slate-600)" }}>
            <div>
              Inventory Shift:{" "}
              <strong>
                {grandTotals.net.itemsDiff >= 0 ? `+${grandTotals.net.itemsDiff}` : grandTotals.net.itemsDiff} Units
              </strong>
            </div>
            <div>•</div>
            <div>
              Margin:{" "}
              <strong>
                {grandTotals.purchases.amount > 0
                  ? ((grandTotals.net.amountDiff / grandTotals.purchases.amount) * 100).toFixed(1) + "%"
                  : "N/A"}
              </strong>
            </div>
          </div>
        </div>
      </div>

      {/* VIEW TABS (On screen toggle for All / Vendors / Clients) */}
      <div
        className="no-print tab-nav-summary"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 18,
          borderBottom: "1.5px solid var(--slate-200, #E2E8F0)",
          paddingBottom: 8,
        }}
      >
        {[
          { id: "all", label: "Combined All (Vendor & Client Tables)", count: filteredVendors.length + filteredClients.length },
          { id: "purchases", label: "Vendor Purchases Only", count: filteredVendors.length, icon: FiTruck },
          { id: "sales", label: "Client Sales Only", count: filteredClients.length, icon: FiUsers },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              backgroundColor: activeTab === tab.id ? "var(--navy, #0F172A)" : "transparent",
              color: activeTab === tab.id ? "#ffffff" : "var(--slate-600, #475569)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
              transition: "all 0.15s ease",
            }}
          >
            {tab.label}
            <span
              style={{
                fontSize: "0.74rem",
                padding: "2px 7px",
                borderRadius: 10,
                backgroundColor: activeTab === tab.id ? "rgba(255,255,255,0.2)" : "#E2E8F0",
                color: activeTab === tab.id ? "#ffffff" : "#475569",
              }}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* ========================================================================= */}
      {/* 1. VENDOR PURCHASES SUMMARY TABLE */}
      {/* ========================================================================= */}
      {(activeTab === "all" || activeTab === "purchases") && (
        <div
          className="table-section"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 12,
            border: "1px solid var(--slate-200, #E2E8F0)",
            boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
            marginBottom: 28,
            overflow: "hidden",
          }}
        >
          {/* Table Header Banner */}
          <div
            style={{
              padding: "14px 20px",
              backgroundColor: "#F8FAFC",
              borderBottom: "1.5px solid var(--slate-200, #E2E8F0)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  backgroundColor: "#EFF6FF",
                  color: "#2563EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiTruck size={17} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "var(--navy, #0F172A)" }}>
                  Vendor Purchases Summary (Inward Inventory)
                </h3>
                <span style={{ fontSize: "0.76rem", color: "var(--slate-500)" }}>
                  Breakdown of purchase bills, units received and amounts per vendor
                </span>
              </div>
            </div>

            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#2563EB" }}>
              Total: {filteredVendors.length} Vendors | Rs. {formatRs(grandTotals.purchases.amount)}
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table className="summary-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#F1F5F9", textAlign: "left", borderBottom: "1.5px solid var(--slate-300)" }}>
                  <th style={{ padding: "10px 14px", width: 50, textAlign: "center", color: "var(--slate-600)" }}>Sr.#</th>
                  <th style={{ padding: "10px 14px", color: "var(--slate-700)" }}>Vendor / Supplier Name</th>
                  <th style={{ padding: "10px 14px", width: 110, textAlign: "center", color: "var(--slate-700)" }}>
                    Total Bills
                  </th>
                  <th style={{ padding: "10px 14px", width: 120, textAlign: "center", color: "var(--slate-700)" }}>
                    Total Items
                  </th>
                  <th style={{ padding: "10px 16px", width: 160, textAlign: "right", color: "var(--slate-700)" }}>
                    Total Amount (Rs.)
                  </th>
                  <th className="no-print" style={{ padding: "10px 14px", width: 80, textAlign: "center" }}>
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ padding: "36px", textAlign: "center", color: "var(--slate-500)" }}>
                      Loading vendor summary data...
                    </td>
                  </tr>
                ) : filteredVendors.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: "36px", textAlign: "center", color: "var(--slate-500)" }}>
                      No vendor purchases recorded for the selected period ({periodLabel}).
                    </td>
                  </tr>
                ) : (
                  filteredVendors.map((vendor, idx) => {
                    const isExpanded = expandedVendors.has(vendor.vendor_id || idx);
                    return (
                      <React.Fragment key={vendor.vendor_id || idx}>
                        <tr
                          style={{
                            borderBottom: "1px solid var(--slate-200)",
                            backgroundColor: idx % 2 === 0 ? "#ffffff" : "#FBFDFE",
                            transition: "background 0.15s ease",
                          }}
                        >
                          <td style={{ padding: "11px 14px", textAlign: "center", fontWeight: 700, color: "var(--slate-500)" }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding: "11px 14px", fontWeight: 800, color: "var(--navy, #0F172A)" }}>
                            <div>{vendor.vendor_name || "—"}</div>
                            {vendor.ntn && (
                              <div style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontWeight: 500 }}>
                                NTN: {vendor.ntn}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <span
                              style={{
                                padding: "3px 9px",
                                borderRadius: 6,
                                backgroundColor: "#EFF6FF",
                                color: "#1D4ED8",
                                fontWeight: 800,
                                fontSize: "0.82rem",
                              }}
                            >
                              {vendor.total_bills} {vendor.total_bills === 1 ? "Bill" : "Bills"}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <span
                              style={{
                                padding: "3px 9px",
                                borderRadius: 6,
                                backgroundColor: "#F1F5F9",
                                color: "var(--navy)",
                                fontWeight: 800,
                                fontSize: "0.84rem",
                              }}
                            >
                              {(parseInt(vendor.total_items) || 0).toLocaleString()} Units
                            </span>
                          </td>
                          <td style={{ padding: "11px 16px", textAlign: "right" }}>
                            <span style={{ fontWeight: 800, color: "#1D4ED8", fontSize: "0.92rem" }}>
                              Rs. {formatRs(vendor.total_amount)}
                            </span>
                          </td>
                          <td className="no-print" style={{ padding: "11px 14px", textAlign: "center" }}>
                            {vendor.bills && vendor.bills.length > 0 ? (
                              <button
                                type="button"
                                onClick={() => toggleVendorExpand(vendor.vendor_id || idx)}
                                title="Expand bills list"
                                style={{
                                  background: "none",
                                  border: "1px solid var(--slate-300)",
                                  borderRadius: 5,
                                  padding: "4px 8px",
                                  cursor: "pointer",
                                  color: "var(--slate-600)",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                {isExpanded ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
                                {isExpanded ? "Hide" : "View"}
                              </button>
                            ) : (
                              <span style={{ color: "var(--slate-400)", fontSize: "0.75rem" }}>—</span>
                            )}
                          </td>
                        </tr>

                        {/* Expanded Bills Details Row */}
                        {isExpanded && vendor.bills && vendor.bills.length > 0 && (
                          <tr style={{ backgroundColor: "#F8FAFC" }}>
                            <td colSpan={6} style={{ padding: "10px 24px 14px 44px", borderBottom: "1.5px solid var(--slate-300)" }}>
                              <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "var(--slate-700)", marginBottom: 6 }}>
                                📄 Inward Bills for {vendor.vendor_name} ({vendor.bills.length}):
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 8 }}>
                                {vendor.bills.map((b, bIdx) => (
                                  <div
                                    key={b.id || bIdx}
                                    style={{
                                      backgroundColor: "#ffffff",
                                      border: "1px solid var(--slate-200)",
                                      borderRadius: 6,
                                      padding: "8px 12px",
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div>
                                      <Link
                                        to={`/purchases/invoice/${encodeURIComponent(b.bill_number)}`}
                                        style={{ fontWeight: 800, color: "#2563EB", textDecoration: "none" }}
                                      >
                                        #{b.bill_number}
                                      </Link>
                                      <div style={{ fontSize: "0.73rem", color: "var(--slate-500)" }}>
                                        📅 {formatDateDisplay(b.purchase_date)} • {b.total_items} units
                                      </div>
                                    </div>
                                    <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.82rem" }}>
                                      Rs. {formatRs(b.total_amount)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>

              {/* VENDOR GRAND TOTAL FOOTER */}
              <tfoot>
                <tr
                  style={{
                    backgroundColor: "#E2E8F0",
                    borderTop: "2.5px solid #0F172A",
                    borderBottom: "2.5px solid #0F172A",
                  }}
                >
                  <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 900, color: "#0F172A" }}>
                    TOTAL
                  </td>
                  <td style={{ padding: "12px 14px", fontWeight: 900, color: "#0F172A" }}>
                    Total {grandTotals.purchases.vendorCount} Vendors
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 900, color: "#1D4ED8" }}>
                    {grandTotals.purchases.billsCount} Bills
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 900, color: "#0F172A" }}>
                    {grandTotals.purchases.itemsCount.toLocaleString()} Units
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 900, color: "#1D4ED8", fontSize: "0.96rem" }}>
                    Rs. {formatRs(grandTotals.purchases.amount)}
                  </td>
                  <td className="no-print" style={{ padding: "12px 14px" }}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. CLIENT SALES SUMMARY TABLE */}
      {/* ========================================================================= */}
      {(activeTab === "all" || activeTab === "sales") && (
        <div
          className="table-section"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 12,
            border: "1px solid var(--slate-200, #E2E8F0)",
            boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
            marginBottom: 28,
            overflow: "hidden",
          }}
        >
          {/* Table Header Banner */}
          <div
            style={{
              padding: "14px 20px",
              backgroundColor: "#F8FAFC",
              borderBottom: "1.5px solid var(--slate-200, #E2E8F0)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  backgroundColor: "#ECFDF5",
                  color: "#059669",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiUsers size={17} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "var(--navy, #0F172A)" }}>
                  Client Sales Summary (Outward Stock & Revenue)
                </h3>
                <span style={{ fontSize: "0.76rem", color: "var(--slate-500)" }}>
                  Breakdown of client sales invoices, items dispatched and total revenue
                </span>
              </div>
            </div>

            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#059669" }}>
              Total: {filteredClients.length} Clients | Rs. {formatRs(grandTotals.sales.amount)}
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table className="summary-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#F1F5F9", textAlign: "left", borderBottom: "1.5px solid var(--slate-300)" }}>
                  <th style={{ padding: "10px 14px", width: 50, textAlign: "center", color: "var(--slate-600)" }}>Sr.#</th>
                  <th style={{ padding: "10px 14px", color: "var(--slate-700)" }}>Client / Customer Name</th>
                  <th style={{ padding: "10px 14px", width: 110, textAlign: "center", color: "var(--slate-700)" }}>
                    Total Invoices
                  </th>
                  <th style={{ padding: "10px 14px", width: 120, textAlign: "center", color: "var(--slate-700)" }}>
                    Total Items
                  </th>
                  <th style={{ padding: "10px 16px", width: 160, textAlign: "right", color: "var(--slate-700)" }}>
                    Total Amount (Rs.)
                  </th>
                  <th className="no-print" style={{ padding: "10px 14px", width: 80, textAlign: "center" }}>
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ padding: "36px", textAlign: "center", color: "var(--slate-500)" }}>
                      Loading client summary data...
                    </td>
                  </tr>
                ) : filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: "36px", textAlign: "center", color: "var(--slate-500)" }}>
                      No client sales recorded for the selected period ({periodLabel}).
                    </td>
                  </tr>
                ) : (
                  filteredClients.map((client, idx) => {
                    const isExpanded = expandedClients.has(client.client_id || idx);
                    return (
                      <React.Fragment key={client.client_id || idx}>
                        <tr
                          style={{
                            borderBottom: "1px solid var(--slate-200)",
                            backgroundColor: idx % 2 === 0 ? "#ffffff" : "#FBFDFE",
                            transition: "background 0.15s ease",
                          }}
                        >
                          <td style={{ padding: "11px 14px", textAlign: "center", fontWeight: 700, color: "var(--slate-500)" }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding: "11px 14px", fontWeight: 800, color: "var(--navy, #0F172A)" }}>
                            <div>{client.client_name || "—"}</div>
                            {client.ntn && (
                              <div style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontWeight: 500 }}>
                                NTN: {client.ntn}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <span
                              style={{
                                padding: "3px 9px",
                                borderRadius: 6,
                                backgroundColor: "#ECFDF5",
                                color: "#047857",
                                fontWeight: 800,
                                fontSize: "0.82rem",
                              }}
                            >
                              {client.total_invoices} {client.total_invoices === 1 ? "Invoice" : "Invoices"}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <span
                              style={{
                                padding: "3px 9px",
                                borderRadius: 6,
                                backgroundColor: "#F1F5F9",
                                color: "var(--navy)",
                                fontWeight: 800,
                                fontSize: "0.84rem",
                              }}
                            >
                              {(parseInt(client.total_items) || 0).toLocaleString()} Units
                            </span>
                          </td>
                          <td style={{ padding: "11px 16px", textAlign: "right" }}>
                            <span style={{ fontWeight: 800, color: "#047857", fontSize: "0.92rem" }}>
                              Rs. {formatRs(client.total_amount)}
                            </span>
                          </td>
                          <td className="no-print" style={{ padding: "11px 14px", textAlign: "center" }}>
                            {client.invoices && client.invoices.length > 0 ? (
                              <button
                                type="button"
                                onClick={() => toggleClientExpand(client.client_id || idx)}
                                title="Expand invoices list"
                                style={{
                                  background: "none",
                                  border: "1px solid var(--slate-300)",
                                  borderRadius: 5,
                                  padding: "4px 8px",
                                  cursor: "pointer",
                                  color: "var(--slate-600)",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                {isExpanded ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
                                {isExpanded ? "Hide" : "View"}
                              </button>
                            ) : (
                              <span style={{ color: "var(--slate-400)", fontSize: "0.75rem" }}>—</span>
                            )}
                          </td>
                        </tr>

                        {/* Expanded Invoices Details Row */}
                        {isExpanded && client.invoices && client.invoices.length > 0 && (
                          <tr style={{ backgroundColor: "#F8FAFC" }}>
                            <td colSpan={6} style={{ padding: "10px 24px 14px 44px", borderBottom: "1.5px solid var(--slate-300)" }}>
                              <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "var(--slate-700)", marginBottom: 6 }}>
                                📄 Outward Sales Invoices for {client.client_name} ({client.invoices.length}):
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 8 }}>
                                {client.invoices.map((inv, invIdx) => (
                                  <div
                                    key={inv.id || invIdx}
                                    style={{
                                      backgroundColor: "#ffffff",
                                      border: "1px solid var(--slate-200)",
                                      borderRadius: 6,
                                      padding: "8px 12px",
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div>
                                      <Link
                                        to={`/sales/invoice/${encodeURIComponent(inv.invoice_number)}`}
                                        style={{ fontWeight: 800, color: "#059669", textDecoration: "none" }}
                                      >
                                        #{inv.invoice_number}
                                      </Link>
                                      <div style={{ fontSize: "0.73rem", color: "var(--slate-500)" }}>
                                        📅 {formatDateDisplay(inv.sale_date)} • {inv.total_items} units
                                      </div>
                                    </div>
                                    <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.82rem" }}>
                                      Rs. {formatRs(inv.total_amount)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>

              {/* CLIENT GRAND TOTAL FOOTER */}
              <tfoot>
                <tr
                  style={{
                    backgroundColor: "#E2E8F0",
                    borderTop: "2.5px solid #0F172A",
                    borderBottom: "2.5px solid #0F172A",
                  }}
                >
                  <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 900, color: "#0F172A" }}>
                    TOTAL
                  </td>
                  <td style={{ padding: "12px 14px", fontWeight: 900, color: "#0F172A" }}>
                    Total {grandTotals.sales.clientCount} Clients
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 900, color: "#047857" }}>
                    {grandTotals.sales.invoicesCount} Invoices
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 900, color: "#0F172A" }}>
                    {grandTotals.sales.itemsCount.toLocaleString()} Units
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 900, color: "#047857", fontSize: "0.96rem" }}>
                    Rs. {formatRs(grandTotals.sales.amount)}
                  </td>
                  <td className="no-print" style={{ padding: "12px 14px" }}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* PRINT-ONLY SIGNATURES FOOTER */}
      <div className="print-only print-footer-signatures">
        <div style={{ textAlign: "center", width: 180 }}>
          <div style={{ borderBottom: "1.5px solid #000", height: 40, marginBottom: 6 }}></div>
          <div style={{ fontSize: "0.8rem", fontWeight: 700 }}>Prepared By</div>
          <div style={{ fontSize: "0.72rem", color: "#475569" }}>{user?.name || "Staff Officer"}</div>
        </div>
        <div style={{ textAlign: "center", width: 180 }}>
          <div style={{ borderBottom: "1.5px solid #000", height: 40, marginBottom: 6 }}></div>
          <div style={{ fontSize: "0.8rem", fontWeight: 700 }}>Accounts / Auditor</div>
          <div style={{ fontSize: "0.72rem", color: "#475569" }}>Verification & Stamp</div>
        </div>
        <div style={{ textAlign: "center", width: 180 }}>
          <div style={{ borderBottom: "1.5px solid #000", height: 40, marginBottom: 6 }}></div>
          <div style={{ fontSize: "0.8rem", fontWeight: 700 }}>Director / CEO</div>
          <div style={{ fontSize: "0.72rem", color: "#475569" }}>Commercial IT Solutions</div>
        </div>
      </div>
    </div>
  );
};

export default SummaryPage;
